////////////////////////////////////////////////////////////////////////////////
//
// Token Service
//
////////////////////////////////////////////////////////////////////////////////
import { jwtDecode } from 'jwt-decode';
const ID_TOKEN_KEY = "id_token";
const ID_USERNAME = "id_username";
const ID_ENTITY = "id_entity";
const ID_TOKEN_REFRESH = "id_token_refresh";
const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes

////////////////////////////////////////////////////////////////////////////////

const TokenService = {
 token: () => {
    const token = window.localStorage.getItem(ID_TOKEN_KEY);
    return token;
  },  tokenSave: (token) => {
    window.localStorage.setItem(ID_TOKEN_KEY, token);
  },
  tokenDelete: () => {
    window.localStorage.removeItem(ID_TOKEN_KEY);
  },

  username: () => window.localStorage.getItem(ID_USERNAME),
  usernameSave: (username) => {
    window.localStorage.setItem(ID_USERNAME, username);
  },
  usernameDelete: () => {
    localStorage.removeItem(ID_USERNAME);
  },

  // -- active entity
  entity: () => window.localStorage.getItem(ID_ENTITY),
  entitySave: (entity) => {
    window.localStorage.setItem(ID_ENTITY, entity);
  },
  entityDelete: () => {
    localStorage.removeItem(ID_ENTITY);
  },

  // -- refresh
  refresh: () => window.localStorage.getItem(ID_TOKEN_REFRESH),
  refreshSave: (token) => {
    localStorage.setItem(ID_TOKEN_REFRESH, token);
  },
  refreshToken: () => {
    return axios.post('/refresh', { 
      refreshToken: TokenService.refresh() 
    }).then(response => {
      if (response.data.result) {
        TokenService.tokenSave(response.data.data.accessToken);
        if (response.data.data.refreshToken) {
          TokenService.refreshSave(response.data.data.refreshToken);
        }
        return response.data.data.accessToken;
      }
      throw new Error('Refresh failed');
    });
  },
  refreshDelete: () => {
    localStorage.removeItem(ID_TOKEN_REFRESH);
  },

 getClaims: () => {
    const token = TokenService.token();
    //console.log('Token being decoded:', token);
    if (token) {
      try {
        const decoded = jwtDecode(token);
       //console.log('Token decoded successfully:', decoded);
        return decoded;
      } catch (error) {
        console.error("Failed to decode token:", error);
       //console.log('Raw token value:', token);
        return null;
      }
    }
    return null;
  },
isValidToken: (token) => {
    if (!token || typeof token !== 'string') return false;
    
    // Basic JWT format check (should have 3 parts separated by dots)
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    try {
      // Try to decode the token to validate it
      jwtDecode(token);
      return true;
    } catch (error) {
      console.error("Token validation failed:", error);
      return false;
    }
  },
  
  // Enhanced token expiration check with better error handling

// In token.service.js
getTokenExpiry: () => {
  const token = TokenService.token();
 //console.log('DEBUG: Getting token expiry for token:', token ? 'exists' : 'missing');
  
  if (!token || !TokenService.isValidToken(token)) {
   //console.log('DEBUG: Token is invalid or missing');
    return null;
  }
  
  try {
    const decoded = jwtDecode(token);
   //console.log('DEBUG: Token decoded successfully, exp:', decoded.exp);
    return decoded.exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.error("DEBUG: Failed to decode token for expiry time:", error);
    return null;
  }
},

isTokenExpired: () => {
  const token = TokenService.token();
 //console.log('DEBUG: Checking if token expired for token:', token ? 'exists' : 'missing');
  
  if (!token) {
   //console.log('DEBUG: No token found, considering expired');
    return true;
  }
  
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    const bufferTime = TOKEN_REFRESH_BUFFER / 1000;
    const isExpired = decoded.exp < (currentTime + bufferTime);
    
    /*console.log('DEBUG: Token expiration details:', {
      exp: decoded.exp,
      currentTime: currentTime,
      bufferTime: bufferTime,
      isExpired: isExpired
    });*/
    
    return isExpired;
  } catch (error) {
    console.error("DEBUG: Failed to decode token for expiration check:", error);
    return true;
  }
},
  
  // Add a method to clear invalid tokens
  clearInvalidTokens: () => {
    if (!TokenService.isValidToken(TokenService.token())) {
      TokenService.tokenDelete();
    }
    // You might want to add similar validation for refresh tokens
  },
/**
 * Check if refresh token exists and is valid
 * @returns {boolean} true if refresh token is available
 */
hasValidRefreshToken: () => {
  const refreshToken = TokenService.refresh();
  return !!refreshToken; // You might want to add expiration check for refresh token too
},
/**
 * Get time until token expiry in milliseconds
 * @returns {number} milliseconds until expiry, or 0 if expired/invalid
 */
getTimeUntilExpiry: () => {
  const token = TokenService.token();
  if (!token) return 0;
  
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    const timeUntilExpiry = (decoded.exp - currentTime) * 1000;
    return Math.max(0, timeUntilExpiry);
  } catch (error) {
    console.error("Failed to decode token for expiry time:", error);
    return 0;
  }
},
/**
 * Clear all authentication data
 */
clearAll: () => {
  TokenService.tokenDelete();
  TokenService.refreshDelete();
  TokenService.usernameDelete();
  TokenService.entityDelete();
}
};

////////////////////////////////////////////////////////////////////////////////

export { TokenService };
